import { isCancelledError } from "client_legacy/ndaw/cancellation";
import createVanilla from "zustand/vanilla";
import { StoreApi } from "zustand";
import { isNotNil } from "client/Misc/utils";

export type LoadingStatus =
	| "loading"
	| "ready"
	| "error";

export type AutoFetchStoreOptions = {
	forceReload?: boolean;
	onError?(): void;
};

export type FetchCallback = (options?: AutoFetchStoreOptions) => void;

export type AutoFetchStoreData<TState> = {
	state: TState;
	status: LoadingStatus;
	fetch: FetchCallback;
};

export function createAutoFetchStore<TState>(
	defaultState: TState,
	fetchData: (signal: AbortSignal) => Promise<TState>
): StoreApi<AutoFetchStoreData<TState>> {
	return createVanilla<AutoFetchStoreData<TState>>(
		(set): AutoFetchStoreData<TState> => {
			let status: InternalStatus = { kind: "pending" };

			return {
				state: defaultState,
				status: "loading",

				async fetch(options: AutoFetchStoreOptions = {}): Promise<void> {
					const {
						forceReload = false,
						onError
					} = options;

					const errorCallbacks: (() => void)[] = [];

					if (status.kind === "loading") {
						errorCallbacks.push(
							...status.errorCallbacks
						);

						status.errorCallbacks = errorCallbacks;
					}

					if (isNotNil(onError)) {
						errorCallbacks.push(onError);
					}

					if (forceReload) {
						if (status.kind === "loading") {
							status.loadingCancellation
								.abort();
						}
						status = { kind: "pending" };
					}

					if (status.kind !== "pending" && status.kind !== "error") {
						return;
					}

					const loadingCancellation = new AbortController();

					status = {
						kind: "loading",
						loadingCancellation,
						errorCallbacks
					};

					set({ status: "loading" });

					try {
						const data: TState = await fetchData(loadingCancellation.signal);

						status = { kind: "ready" };

						set({
							state: data,
							status: "ready"
						});
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						status = { kind: "error" };
						set({ status: "error" });

						errorCallbacks.forEach(
							(callback) => callback()
						);

						console.error(e);
					}
				}
			};
		}
	);
}

type InternalStatus = {
	kind: "pending";
} | {
	kind: "loading";
	loadingCancellation: AbortController;
	errorCallbacks: readonly (() => void)[];
} | {
	kind: "ready";
} | {
	kind: "error";
};